import { memo, Suspense, useState } from "react";
import { loadQuery, PreloadedQuery, usePreloadedQuery } from "react-relay";
import { useLoaderData } from "react-router-dom";
import graphql from "babel-plugin-relay/macro";

import { InspectionDetailsContainer } from "./components/InspectionDetailsContainer";
import { InspectionDetailsPageQuery as QT } from "./__generated__/InspectionDetailsPageQuery.graphql";
import { InspectionHeader } from "./components/InspectionHeader";
import { InspectionFieldMap } from "./components/InspectionFieldMap";
import { InspectionfieldStateEnum } from "./components/__generated__/InspectionDetailsContainer.graphql";
import Button from "../../components/Buttons/Button";
import InspectionPlaceholderCardContainer from "../../components/InspectionFormOverview";
import InspectionReportOverview from "../../components/InspectionReportOverview";
import PlanModal from "../../components/PlanModal";
import RelayEnvironment from "../../relay/RelayEnvironment";
import Sidebar from "../../components/Sidebar";

import {
  ColumnHeader,
  ColumnLeft,
  ColumnRight,
  ColumnsContainer,
  Container,
  DetailColumn,
} from "./Styles";

const InspectionDetailsQuery = graphql`
  query InspectionDetailsPageQuery($id: ID!) {
    inspectionfield(id: $id) {
      id
      externalId
      year
      state
      plannedDate
      ...InspectionHeader
      ...InspectionFieldMap
      ...InspectionFormOverview
      ...InspectionReportOverview
      ...InspectionDetailsContainer
    }
  }
`;

export const loader = ({ params }: any) => {
  return loadQuery<QT>(RelayEnvironment, InspectionDetailsQuery, {
    id: params.id!,
  });
};

export const InspectionDetailsPageContainer = memo(function Detail() {
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false);
  const queryRef = useLoaderData() as PreloadedQuery<QT>;
  const data = usePreloadedQuery<QT>(InspectionDetailsQuery, queryRef);
  if (!data?.inspectionfield) {
    return null;
  }
  return (
    <Container>
      <Sidebar />
      <ColumnsContainer>
        <ColumnLeft>
          <InspectionHeader inspectionfield={data.inspectionfield} />
          <InspectionPlaceholderCardContainer
            inspectionfield={data.inspectionfield}
            onPlan={() => setShowPlanModal(true)}
          />
          <InspectionReportOverview inspectionField={data.inspectionfield} />
        </ColumnLeft>
        <ColumnRight>
          <ColumnHeader>
            <StateButton
              state={data.inspectionfield.state}
              onPlan={() => setShowPlanModal(true)}
            />
          </ColumnHeader>
          <DetailColumn>
            <h3>Schouwdetails</h3>
            <Suspense fallback={<>...</>}>
              <InspectionDetailsContainer
                onPlan={() => setShowPlanModal(true)}
                inspectionfield={data.inspectionfield}
              />
            </Suspense>
            <InspectionFieldMap inspectionfield={data?.inspectionfield} />
          </DetailColumn>
        </ColumnRight>
      </ColumnsContainer>
      {data.inspectionfield.id && showPlanModal ? (
        <PlanModal
          show={showPlanModal}
          selectedInspections={[
            {
              id: data.inspectionfield.id,
              externalId: data.inspectionfield.externalId || 0,
              year: data.inspectionfield.year,
            },
          ]}
          date={data.inspectionfield.plannedDate || undefined}
          showDelete={Boolean(data.inspectionfield.plannedDate)}
          onDismiss={() => setShowPlanModal(false)}
        />
      ) : null}
    </Container>
  );
});

export const StateButton = memo(function Comp(props: {
  state: InspectionfieldStateEnum | null;
  onPlan?: Function;
}) {
  return ["PLANNED", "ASSIGNED"].includes(props.state || "") ? (
    <Button
      icon="pencil"
      label={props.state === "PLANNED" ? "Datum aanpassen" : "Inplannen"}
      bordered={props.state === "PLANNED"}
      color={props.state !== "PLANNED" ? "blue" : "white"}
      onClick={() => props.onPlan && props.onPlan()}
    />
  ) : null;
});

export default InspectionDetailsPageContainer;
